import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Circle, Eye, Check } from "lucide-react";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";

import { useGetDataBlockVersionsQuery } from "@/redux/service";
import BaseTimeline from "@/components_v2/Timeline/BaseTimeline";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import BaseDivider from "@/components_v2/Divider/BaseDivider";
import DataBlockReferences from "@/features/datablocks/details/components/DataBlockReferences";
import {
  CardVariant,
  DataBlockVersionsProps,
} from "@/features/datablocks/details/types";
import { getEvidenceTitle } from "@/domains/evidence/hooks/useEvidenceFields";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { DataBlockVersionResponse } from "@/redux/datablocks/types";
import { PAGINATION, Roles, SharingRights } from "@/shared/utils/constants";
import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { CARD_VARIANTS } from "@/features/datablocks/details/constants";
import { selectCurrentUser } from "@/redux/authentication/selectors";

import "../styles.css";

const getCardVariant = (
  version: DataBlockVersionResponse,
  currentVersionId: number,
): CardVariant => {
  if (version.editable && version.id === currentVersionId) {
    return CARD_VARIANTS.ACTIVE;
  }

  if (version.editable) {
    return CARD_VARIANTS.PAST;
  }

  if (version.id === currentVersionId) {
    return CARD_VARIANTS.SELECTED;
  }

  return CARD_VARIANTS.PAST;
};

const getVersionTimelineDot = (
  version: DataBlockVersionResponse,
  isViewingCurrentVersion: boolean,
) => {
  if (version.editable && isViewingCurrentVersion) {
    return <Circle color="var(--green-500)" strokeWidth={4} size={20} />;
  }
  if (version.editable && !isViewingCurrentVersion) {
    return <Circle color="var(--neutral-500)" strokeWidth={4} size={20} />;
  }
  if (!version.editable && isViewingCurrentVersion) {
    return (
      <div className="data-block-version-timeline-icon data-block-version-timeline-icon--selected">
        <Check strokeWidth={3} size={16} color="var(--base-white)" />
      </div>
    );
  }
  return (
    <div className="data-block-version-timeline-icon">
      <Check strokeWidth={3} size={16} color="var(--base-white)" />
    </div>
  );
};

const DataBlockVersions = ({ dataBlockId }: DataBlockVersionsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [allVersions, setAllVersions] = useState<DataBlockVersionResponse[]>(
    [],
  );
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const currentUser = useSelector(selectCurrentUser);

  const { data, isLoading, isFetching } = useGetDataBlockVersionsQuery({
    id: dataBlockId,
    page,
    pageSize: PAGINATION.PAGE_SIZE.DATA_BLOCK_VERSION,
  });

  useEffect(() => {
    if (!data) return;
    const results = isPaginatedResponse(data) ? data.results : data;
    const next = isPaginatedResponse(data) ? data.next : null;

    setAllVersions((prev) => {
      const existingIds = new Set(prev.map((v) => v.id));
      const newItems = results.filter((v) => !existingIds.has(v.id));
      return [...prev, ...newItems];
    });
    setHasMore(!!next);
  }, [data]);

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && entry.isIntersecting && !isFetching) loadMore();
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, isFetching]);

  const timelineItems = useMemo(() => {
    if (!allVersions.length) return [];

    const sorted = [...allVersions].sort((a, b) => {
      if (a.editable && !b.editable) return -1;
      if (!a.editable && b.editable) return 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return sorted.map((version) => {
      const variant = getCardVariant(version, dataBlockId);
      const currentVersionSurvey = version.references.surveys[0];
      const editionTitle = currentVersionSurvey
        ? currentVersionSurvey.label
        : getEvidenceTitle(version);

      const isViewingCurrentVersion = version.id === dataBlockId;
      const shouldShowViewButton =
        !version.editable &&
        version.id !== dataBlockId &&
        (version.sharingRights !== SharingRights.PRIVATE.value ||
          (currentUser && currentUser.role === Roles.ADMIN.value));

      return {
        key: version.id,
        dot: getVersionTimelineDot(version, isViewingCurrentVersion),
        children: (
          <div
            className={`data-block-version-card data-block-version-card--${variant}`}
          >
            <div className="flex flex-col gap-200">
              <div className="flex flex-col gap-100">
                <div className="flex items-center gap-100 justify-between flex-wrap">
                  <h3 className="body-m-medium">
                    {version.editable
                      ? t("datablocks.labels.active")
                      : t("datablocks.labels.evidenceEdition", {
                          title: editionTitle,
                        })}
                  </h3>

                  {shouldShowViewButton && (
                    <BaseButton
                      variant={BUTTON_VARIANTS.LINK}
                      size={BUTTON_SIZES.EXTRA_SMALL}
                      leadingIcon={<Eye size={16} />}
                      onClick={() => {
                        navigate(`/evidence/${version.id}/`);
                      }}
                    >
                      {t("datablocks.labels.view")}
                    </BaseButton>
                  )}
                </div>

                <p className="body-s-regular data-block-version-description">
                  {!version.editable &&
                    t("datablocks.labels.versionSubmissionDate", {
                      date: DateTime.fromISO(version.createdAt).toFormat(
                        "dd MMMM yyyy",
                      ),
                    })}
                </p>

                <BaseDivider />
              </div>

              <DataBlockReferences references={version.references} />
            </div>
          </div>
        ),
      };
    });
  }, [allVersions, dataBlockId, t]);

  if (isLoading) return null;

  return (
    <div className="flex flex-col gap-300">
      <BaseTimeline items={timelineItems} />

      {hasMore && (
        <div ref={sentinelRef} className="flex justify-center">
          {isFetching && <BaseSpinner size="small" />}
        </div>
      )}
    </div>
  );
};

export default DataBlockVersions;
